import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import BotBacktestMain from '../bot/components/BotBacktestMain'
import { botApi, type BacktestCodeResult, type BacktestResult, type BotCode } from '../bot/api'

const HEADER_HEIGHT = 56

export default function BotBacktestPage() {
  const { btid: rawBtid } = useParams<{ btid: string }>()
  const btid = rawBtid?.trim() ?? ''

  const [backtest, setBacktest] = useState<BacktestResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [backtestCode, setBacktestCode] = useState<BacktestCodeResult | null>(null)

  useEffect(() => {
    if (!btid) {
      setBacktest(null)
      setIsLoading(false)
      setErrorMessage(null)
      return
    }

    let cancelled = false
    setIsLoading(true)
    setBacktest(null)
    setErrorMessage(null)

    botApi.getBacktestByBtid(btid)
      .then(data => {
        if (cancelled) return
        if (!data) {
          setErrorMessage('Backtest not found.')
          return
        }
        setBacktest(data)
      })
      .catch(err => {
        if (cancelled) return
        const isNotFound = err?.errorCode === 'BACKTEST_NOT_FOUND'
        if (isNotFound) {
          setErrorMessage('Backtest not found.')
          return
        }
        setErrorMessage('Failed to load backtest. Please try again.')
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [btid])

  useEffect(() => {
    if (!btid) {
      setBacktestCode(null)
      return
    }

    let cancelled = false
    setBacktestCode(null)

    botApi.getBacktestCode(btid)
      .then(data => {
        if (cancelled) return
        if (!data) return
        setBacktestCode(data)
      })
      .catch(() => {
        // The bottom Code tab renders an empty state when no code is provided,
        // so swallow errors here rather than surfacing a top-level failure.
      })

    return () => {
      cancelled = true
    }
  }, [btid])

  const currentCode = useMemo<BotCode | null>(() => {
    if (!backtestCode) return null
    return {
      codeId: backtestCode.codeId,
      cid: backtestCode.cid,
      version: backtestCode.version,
      code: backtestCode.code,
      strategyDescription: backtestCode.strategyDescription,
      created: backtestCode.created,
    }
  }, [backtestCode])

  if (!btid) {
    return (
      <div
        className="flex items-center justify-center h-full text-sm"
        style={{ color: 'var(--color-error)' }}
      >
        Invalid backtest ID. Use /backtest/&lt;btid&gt; in the URL.
      </div>
    )
  }

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center h-full text-sm"
        style={{ color: 'var(--color-text-grey)' }}
      >
        Loading backtest...
      </div>
    )
  }

  if (errorMessage || !backtest) {
    return (
      <div
        className="flex items-center justify-center h-full text-sm"
        style={{ color: 'var(--color-error)' }}
      >
        {errorMessage || 'Backtest not found.'}
      </div>
    )
  }

  const backToBotHref = backtestCode?.bid || backtest.bid ? `/strategy/${backtestCode?.bid || backtest.bid}` : ''

  return (
    <div
      style={{
        position: 'relative',
        height: '100%',
        backgroundColor: 'var(--base-100)',
        overflow: 'hidden'
      }}
    >
      <div
        className="px-4"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: HEADER_HEIGHT,
          borderBottom: '1px solid var(--color-line)',
          backgroundColor: 'var(--base-200)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px'
        }}
      >
        <div className="inline-flex items-center gap-2">
          {backToBotHref ? (
            <Link
              to={backToBotHref}
              className="inline-flex items-center gap-2 text-sm"
              style={{
                color: 'var(--base-content)',
                textDecoration: 'none'
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m15 18-6-6 6-6" />
              </svg>
              <span>Back to builder</span>
            </Link>
          ) : (
            <div className="inline-flex items-center gap-2 text-sm" style={{ color: 'var(--color-text-grey)' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m15 18-6-6 6-6" />
              </svg>
              <span>Back to builder</span>
            </div>
          )}
          {backtest.version != null && (
            <VersionTag version={backtest.version} codeVersion={backtest.codeVersion} />
          )}
        </div>
      </div>

      <div
        style={{
          position: 'absolute',
          top: HEADER_HEIGHT,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden'
        }}
      >
        {backtest.status === 0 && (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <div
              className="animate-spin rounded-full h-8 w-8 border-b-2"
              style={{ borderColor: 'var(--color-primary)' }}
            />
            <span className="text-sm" style={{ color: 'var(--color-text-grey)' }}>
              Your backtest has been queued and will begin processing shortly.
            </span>
          </div>
        )}

        {backtest.status === 200 && (
          <div className="flex flex-col items-center justify-center h-full gap-2 px-6">
            <span className="text-sm font-medium" style={{ color: 'var(--color-error)' }}>
              Backtest failed
            </span>
            <span className="text-sm text-center" style={{ color: 'var(--color-text-grey)' }}>
              {backtest.resultSummary?.error || 'An unexpected error occurred during backtest execution. Please try running the backtest again.'}
            </span>
          </div>
        )}

        {backtest.status === 201 && (
          <div className="flex flex-col items-center justify-center h-full gap-2 px-6">
            <span className="text-sm font-medium" style={{ color: 'var(--color-text-grey)' }}>
              Backtest canceled
            </span>
            <span className="text-sm text-center" style={{ color: 'var(--color-text-grey)' }}>
              This backtest was canceled before it could complete. You can run a new backtest at any time.
            </span>
          </div>
        )}

        {backtest.status !== 0 && backtest.status !== 200 && backtest.status !== 201 && (
          <BotBacktestMain
            btid={btid}
            backtest={backtest}
            currentCode={currentCode}
            hasCode={!!currentCode}
          />
        )}
      </div>
    </div>
  )
}

function VersionTag({ version, codeVersion }: { version?: number; codeVersion?: number }) {
  const label =
    codeVersion != null && version != null
      ? `v${codeVersion}.${version}`
      : version != null
        ? `v${version}`
        : null

  if (!label) {
    return null
  }

  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono"
      style={{
        backgroundColor: 'var(--base-300)',
        color: 'var(--color-text-grey)',
        border: '1px solid var(--color-line)'
      }}
    >
      {label}
    </span>
  )
}
